import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Footer } from '../components/Footer';
import { QuickQuote } from '../components/QuickQuote';
import { Toaster } from '../components/ui/sonner';
import { company } from '../mock';
import { popularRoutes } from '../data/transferRoutes';
import {
  Clock, MapPin, ShieldCheck, Star, Car, Wallet, PhoneCall,
  CheckCircle, ChevronRight, Plane, MessageCircle
} from 'lucide-react';

const statIcon = (k) => ({
  clock: <Clock className="w-5 h-5" />,
  pin: <MapPin className="w-5 h-5" />,
  wallet: <Wallet className="w-5 h-5" />
}[k] || <Star className="w-5 h-5" />);

export const TransferPage = ({ data }) => {
  const [language] = useState('en');

  useEffect(() => {
    document.title = data.seo.title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', data.seo.description);
    window.scrollTo(0, 0);
  }, [data]);

  const Section = ({ icon, heading, paragraphs }) => (
    <>
      <h3 className="heading-font text-2xl font-medium text-[#0F172A] mt-10 mb-4 flex items-center gap-2">
        {icon} {heading}
      </h3>
      {paragraphs.map((p, i) => (
        <p key={i} className="text-[#334155] leading-relaxed mb-5">{p}</p>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Toaster position="top-center" />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3" data-testid="seo-logo-home">
            <div className="w-11 h-11 bg-gradient-to-br from-[#D4AF37] to-[#B48E2D] rounded-sm flex items-center justify-center">
              <span className="text-white font-bold text-lg heading-font">EG</span>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-[#0F172A] heading-font leading-none">{company.short}</h1>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mt-1">{company.tagline}</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <a href={`tel:${company.phoneRaw}`} className="hidden sm:flex items-center gap-1 text-sm text-[#475569] hover:text-[#D4AF37] transition-colors">
              <PhoneCall className="w-4 h-4" />{company.phone}
            </a>
            <a href="#book" data-testid="seo-header-book">
              <Button className="bg-[#D4AF37] hover:bg-[#B48E2D] text-white rounded-sm px-6">Book Now</Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={data.hero.image} alt={data.hero.alt} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/90 via-[#0F172A]/70 to-[#0F172A]/40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <nav className="text-xs text-gray-300 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-[#D4AF37]">{data.breadcrumb}</span>
          </nav>
          <p className="eyebrow text-[#D4AF37] mb-4">{data.hero.eyebrow}</p>
          <h2 className="heading-font text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-[1.05] max-w-3xl mb-6">
            {data.hero.h1}
          </h2>
          <p className="text-lg text-gray-200 font-light max-w-2xl mb-8 leading-relaxed">{data.hero.intro}</p>
          <div className="flex flex-wrap gap-4">
            <a href="#book">
              <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B48E2D] text-white rounded-sm px-8 py-6 text-base" data-testid="seo-hero-book">
                Get a Free Quote <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href={`https://wa.me/${company.whatsapp}`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border border-white/60 bg-white/5 text-white hover:bg-white hover:text-[#0F172A] rounded-sm px-8 py-6 text-base">
                <MessageCircle className="mr-2 w-5 h-5" /> WhatsApp Us
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-3 gap-6 mt-12 max-w-xl">
            {data.stats.map((s) => (
              <div key={s.bottom} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-white/10 border border-white/15 flex items-center justify-center text-[#D4AF37]">{statIcon(s.icon)}</div>
                <div>
                  <p className="text-white font-semibold text-sm">{s.top}</p>
                  <p className="text-gray-300 text-xs">{s.bottom}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <h3 className="heading-font text-3xl font-medium text-[#0F172A] mb-4">{data.intro.heading}</h3>
        {data.intro.paragraphs.map((p, i) => (
          <p key={i} className="text-[#334155] leading-relaxed mb-5">{p}</p>
        ))}

        <Section icon={<Clock className="w-6 h-6 text-[#D4AF37]" />} heading={data.travelTime.heading} paragraphs={data.travelTime.paragraphs} />

        <h3 className="heading-font text-2xl font-medium text-[#0F172A] mt-10 mb-4 flex items-center gap-2">
          <Star className="w-6 h-6 text-[#D4AF37]" /> Reasons to choose a private transfer
        </h3>
        <div className="space-y-3 mb-6">
          {data.reasons.map(([title, text]) => (
            <div key={title} className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <p className="text-[#334155] leading-relaxed"><strong className="text-[#0F172A]">{title}.</strong> {text}</p>
            </div>
          ))}
        </div>

        <Section icon={<Car className="w-6 h-6 text-[#D4AF37]" />} heading={data.vehicles.heading} paragraphs={data.vehicles.paragraphs} />
        <Section icon={<ShieldCheck className="w-6 h-6 text-[#D4AF37]" />} heading={data.howItWorks.heading} paragraphs={data.howItWorks.paragraphs} />

        <p className="text-[#334155] leading-relaxed mt-2">
          Explore our full range of private tours and transfers across Lisbon, Sintra, Cascais and the Algarve on our <Link to="/" className="text-[#D4AF37] hover:text-[#B48E2D] underline">homepage</Link>.
        </p>
      </article>

      {/* Booking CTA */}
      <section id="book" className="bg-white border-y border-[#E2E8F0] py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow text-[#D4AF37] mb-3">Reserve Now</p>
            <h3 className="heading-font text-3xl sm:text-4xl font-medium text-[#0F172A] mb-4">{data.bookingHeading}</h3>
            <p className="text-[#475569] font-light leading-relaxed mb-6">
              Get a free, no-obligation quote in seconds. We reply within minutes with a fixed price.
            </p>
            <ul className="space-y-2">
              {['Free quote, no obligation', 'Flight tracking & meet and greet', 'Fixed price — taxes & tolls included'].map((x) => (
                <li key={x} className="flex items-center text-[#0F172A]"><CheckCircle className="w-4 h-4 text-[#D4AF37] mr-2" />{x}</li>
              ))}
            </ul>
          </div>
          <QuickQuote language={language} />
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <p className="eyebrow text-[#D4AF37] mb-3">FAQ</p>
        <h3 className="heading-font text-3xl sm:text-4xl font-medium text-[#0F172A] mb-8">Frequently asked questions</h3>
        <div className="space-y-5">
          {data.faqs.map((f, i) => (
            <div key={f.q} className="bg-white border border-[#E2E8F0] rounded-sm p-6" data-testid={`faq-item-${i}`}>
              <h4 className="font-semibold text-[#0F172A] mb-2">{f.q}</h4>
              <p className="text-[#475569] leading-relaxed font-light">{f.a}</p>
            </div>
          ))}
        </div>

        {/* Other popular routes (internal links) */}
        <div className="mt-10">
          <p className="text-sm font-semibold text-[#0F172A] mb-3">Other popular routes</p>
          <div className="flex flex-wrap gap-3">
            {popularRoutes.filter((r) => r.slug !== data.slug).map((r) => (
              <Link key={r.slug} to={`/${r.slug}`} className="text-sm bg-white border border-[#E2E8F0] hover:border-[#D4AF37] rounded-sm px-4 py-2 text-[#475569] hover:text-[#D4AF37] transition-colors" data-testid={`related-${r.slug}`}>
                {r.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-10 text-center bg-[#0F172A] rounded-sm p-8">
          <Plane className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
          <h4 className="heading-font text-2xl text-white font-medium mb-2">Ready to ride?</h4>
          <p className="text-gray-300 font-light mb-5">Book your private transfer now or message us on WhatsApp.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="#book"><Button className="bg-[#D4AF37] hover:bg-[#B48E2D] text-white rounded-sm px-8">Get a Free Quote</Button></a>
            <Link to="/"><Button variant="outline" className="border-white/40 text-white hover:bg-white hover:text-[#0F172A] rounded-sm px-8 bg-transparent">Back to Homepage</Button></Link>
          </div>
        </div>
      </section>

      <Footer language={language} />
    </div>
  );
};

export default TransferPage;
